import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ButtonTip } from "@/components/ui/button-tip";
import { OnboardingApiError, onboardingPublicAPI, type SlimLeadView } from "../OnboardingAPI";
import { clearHandle, readHandle } from "./portalHandle";

/** `not_ready` (the account is still being made) is tried again after this long, this many times at most. */
export const PORTAL_RETRY_MS = 3000;
export const PORTAL_RETRY_MAX = 5;

/** Where the one-time link may lead: the portal's own sign-in route, nowhere else. */
const isMagicPath = (url: unknown): url is string => typeof url === "string" && /^\/auth\/magic\?token=[^\s&#]+$/.test(url);

/**
 * "Direkt zum Portal" on the thank-you scene: once the account is ready (`accountReady`) and this tab holds the handle
 * the submit gave, one click signs the person in through the portal's own magic-link route. Never without a click.
 * Without a handle (another tab or browser), or when the server refuses it, there is no button - only the sentence
 * that the access data from the e-mail work, and a link to the sign-in. The handle lives in this tab's sessionStorage
 * alone and is removed once it was used or the server says it is no good.
 */
export default function PortalAccess({ token, slim }: { token: string; slim: SlimLeadView }) {
  const { t } = useTranslation();
  const [handle, setHandle] = useState<string | null>(() => readHandle(token));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const retryTimer = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(retryTimer.current);
    };
  }, []);

  const ready = slim.accountReady === true;
  const showButton = !!handle && ready && !failed;
  // one quiet announcement when the button appears (it does not take the focus)
  const [announced, setAnnounced] = useState(false);
  useEffect(() => {
    if (showButton) setAnnounced(true);
  }, [showButton]);

  const giveUp = () => {
    clearHandle(token);
    setHandle(null);
    setFailed(true);
    setBusy(false);
    setWaiting(false);
  };

  const signIn = async (attempt = 1, used = handle) => {
    if (!used) return;
    setBusy(true);
    setWaiting(false);
    try {
      const { url } = await onboardingPublicAPI.portalLogin(token, used);
      if (!mounted.current) return;
      if (!isMagicPath(url)) {
        giveUp();
        return;
      }
      clearHandle(token);
      window.location.assign(url);
    } catch (error) {
      if (!mounted.current) return;
      if (error instanceof OnboardingApiError && error.code === "not_ready") {
        if (attempt < PORTAL_RETRY_MAX) {
          retryTimer.current = window.setTimeout(() => void signIn(attempt + 1, used), PORTAL_RETRY_MS);
          return;
        }
        // still not there: the handle stays good, the person may try again
        setBusy(false);
        setWaiting(true);
        return;
      }
      // invalid, used, expired, unavailable, or any other failure: the e-mail is the way in
      giveUp();
    }
  };

  const fallback = failed || (!handle && slim.accessSent === true);

  return (
    <div className="flex flex-col items-center gap-3" data-testid="portal-access">
      <p className="sr-only" role="status" aria-live="polite" data-testid="portal-ready-live">
        {announced && showButton ? t("onboardingPage.portal.ready") : ""}
      </p>
      <div
        className="grid transition-[grid-template-rows,opacity] duration-500 motion-reduce:transition-none"
        style={{ gridTemplateRows: showButton ? "1fr" : "0fr", opacity: showButton ? 1 : 0 }}
        aria-hidden={showButton ? undefined : true}
        data-testid="portal-button-wrap"
      >
        <div className="min-h-0 overflow-hidden">
          {(showButton || busy) && (
            <div className="flex flex-col items-center gap-2 py-1">
              <ButtonTip label={t("onboardingPage.portal.tip")}>
              <Button type="button" size="lg" onClick={() => void signIn()} disabled={busy} aria-describedby="portal-tip" data-testid="portal-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : null}
                {busy ? t("onboardingPage.portal.signingIn") : t("onboardingPage.portal.button")}
              </Button>
              </ButtonTip>
              <span id="portal-tip" className="sr-only">{t("onboardingPage.portal.tip")}</span>
              {waiting && (
                <p role="status" className="text-sm text-muted-foreground" data-testid="portal-waiting">
                  {t("onboardingPage.portal.stillWaiting")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {fallback && (
        <div className="onb-soft space-y-1 text-sm text-muted-foreground" data-testid="portal-fallback">
          <p>{t("onboardingPage.portal.useEmail")}</p>
          <a href="/login" className="font-medium text-foreground underline underline-offset-4" data-testid="portal-login-link">
            {t("onboardingPage.portal.toLogin")}
          </a>
        </div>
      )}
    </div>
  );
}
